import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { EmptyState, ProductCard, Skeleton } from "@aurelia/ui";
import { useProducts } from "../../features/catalog/queries";
import { useWishlist, useToggleWishlist } from "../../features/wishlist/queries";
import { useDocumentMeta } from "../../lib/useDocumentMeta";
import { useJsonLd } from "../../lib/useJsonLd";
import { SITE_URL, SITE_NAME } from "../../lib/seoConstants";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" }
] as const;

export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sort, setSort] = useState<(typeof SORT_OPTIONS)[number]["value"]>("newest");
  const { data, isLoading, isError } = useProducts({ categorySlug: slug, sort });
  const { data: wishlist } = useWishlist();
  const toggleWishlist = useToggleWishlist();
  const wishlistedIds = new Set(wishlist?.map((p) => p.id));

  const title = slug
    ? slug
        .split("-")
        .map((w) => w[0]?.toUpperCase() + w.slice(1))
        .join(" ")
    : "";

  useDocumentMeta(
    title || "Shop",
    title ? `Shop ${title} at ${SITE_NAME} — wholesale and retail prices, fast delivery across Pakistan.` : undefined
  );
  useJsonLd(
    "breadcrumb-jsonld",
    title
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: title, item: `${SITE_URL}/category/${slug}` }
          ]
        }
      : null
  );

  return (
    <main className="max-w-7xl mx-auto px-6 py-16">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-10">
        <h1 className="text-display-md">{title}</h1>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
          aria-label="Sort products"
          className="h-10 rounded-full border border-border bg-surface px-4 text-sm text-ink shrink-0"
        >
          {SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {isLoading && Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-square" />)}
        {data?.items.map((p) => (
          <ProductCard
            key={p.id}
            slug={p.slug}
            name={p.name}
            shortDescription={p.shortDescription}
            imageUrl={p.images[0] ?? null}
            basePrice={p.basePrice}
            compareAtPrice={p.compareAtPrice}
            soldCount={p.soldCount}
            stock={p.variants.reduce((sum, v) => sum + v.stock, 0)}
            wishlisted={wishlistedIds.has(p.id)}
            onToggleWishlist={() => toggleWishlist.mutate(p.id)}
          />
        ))}
        {isError && (
          <EmptyState tone="error" title="Couldn't load products" description="Please refresh and try again." />
        )}
        {!isLoading && !isError && data?.items.length === 0 && (
          <EmptyState
            title="No products in this category yet"
            description="New stock is added regularly — check back soon, or browse everything we currently carry."
            action={
              <Link
                to="/"
                className="mt-1 inline-flex items-center h-11 px-6 rounded-full bg-ink text-canvas text-sm font-medium hover:bg-accent hover:text-ink transition-colors duration-300 dark:bg-accent dark:text-ink"
              >
                Browse everything
              </Link>
            }
          />
        )}
      </div>
    </main>
  );
}
